/**
 * Recovers from stale asset references after a deploy.
 *
 * Vite emits hashed chunk filenames. When a deploy lands while a user has the
 * app open, the old hashed files stop existing, so the next lazy import in that
 * still-open session 404s and whatever feature triggered it is dead until the
 * user manually reloads. Reported as
 * `TypeError: Failed to fetch dynamically imported module: .../assets/<name>-<hash>.js`
 * (BANYAN_APP-SC).
 *
 * Vite dispatches a cancelable `vite:preloadError` on window and then rethrows
 * unless the event was canceled:
 *
 *     window.dispatchEvent(e)
 *     if (!e.defaultPrevented) { throw err }
 *
 * We never cancel it. A canceled event makes the failed `import()` resolve to
 * `undefined` instead of rejecting, and the caller then fails somewhere
 * unrelated: Capacitor turns it into `"<plugin>" plugin is not implemented on
 * web` (BANYAN_APP-5NB). The import rejects as usual and the page reloads.
 */

const LAST_RELOAD_KEY = 'chunkLoadErrorReloadedAt'
/**
 * A reload only helps if the assets really did change. If they did not — a
 * genuinely missing file, a broken CDN — reloading again would spin. Allow one
 * reload per cooldown window rather than one per session, so a long-lived
 * session can still recover from a second, later deploy.
 */
const RELOAD_COOLDOWN_MS = 60_000

const CHUNK_LOAD_ERROR_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type|error loading dynamically imported module|Unable to preload CSS/

let isReloading = false

/** Claims the reload slot. Returns false if we must not reload right now. */
const claimReloadSlot = (): boolean => {
  try {
    const lastReloadedAt = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY)) || 0

    if (Date.now() - lastReloadedAt < RELOAD_COOLDOWN_MS) return false

    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))

    return true
  } catch {
    // sessionStorage can throw (Safari private mode, quota). Without a usable
    // guard, an endless reload loop would be worse than leaving the stale chunk
    // unrecovered, so decline.
    return false
  }
}

export const isChunkLoadErrorBeingRecovered = (error: unknown): boolean =>
  isReloading && error instanceof Error && CHUNK_LOAD_ERROR_MESSAGE.test(error.message)

export const initChunkLoadErrorRecovery = () => {
  // Not using the `vite:preloadError` WindowEventMap augmentation from
  // `vite/client`, which is not in this package's tsconfig `types`.
  window.addEventListener('vite:preloadError', () => {
    if (!claimReloadSlot()) return

    isReloading = true

    window.location.reload()
  })
}
